import {
    NoonReportActivitiesSectionBM,
    NoonReportBridgeSectionBM,
    NoonReportCargoHeatingSectionBM,
    NoonReportCommentsSectionBM,
    NoonReportEnginesSectionBM,
    NoonReportFreshWaterSectionBM,
    NoonReportFuelRobSectionBM,
    NoonReportGeneralSectionBM,
    NoonReportInertGasProductionSectionBM,
    NoonReportMetadataBM,
    NoonReportMrvSectionBM,
    NoonReportPowerpacksSectionBM,
    NoonReportTankCleaningSectionBM,
    NoonReportWeatherSectionBM,
    ValidatedNoonReportBM,
} from '../types/BusinessModel/ValidatedNoonReportBM';
import {
    NoonReportActivitiesSection,
    NoonReportBridgeSection,
    NoonReportCargoHeatingSection,
    NoonReportCommentsSection,
    NoonReportEnginesSection,
    NoonReportFreshWaterSection,
    NoonReportFuelRobSection,
    NoonReportGeneralSection,
    NoonReportInertGasProductionSection,
    NoonReportMetadata,
    NoonReportMrvSection,
    NoonReportPowerpacksSection,
    NoonReportTankCleaningSection,
    NoonReportWeatherSection,
    ValidatedNoonReport,
} from '../types/ViewModel/ValidatedNoonReport';
import { CargoTankTemperatureFactory } from './CargoTankTemperatureFactory';
import { CargoTemperatureFactory } from './CargoTemperatureFactory';
import { EngineDataFactory } from './EngineDataFactory';
import { EvaporatorDataFactory } from './EvaporatorDataFactory';
import { FuelRobDataFactory } from './FuelRobDataFactory';
import { HeaterFactory } from './HeaterFactory';
import { MrvDataFactory } from './MrvDataFactory';
import { NoonReportDateTimeFactory } from './NoonReportDateTimeFactory';
import { NitrogenGeneratorDataFactory } from './NitrogenGeneratorDataFactory';
import { NoonReportSectionDataFactory } from './NoonReportSectionDataFactory';
import { getAolDateFromTime, getDirectionEnum, getSeaStateEnum, getWindForceEnum } from '../utils/aolFieldsHelper';
import { convertToDate } from 'odfjell-date-time-utils/dateTimeConverter';
import { BoilerConsumptionV5BM } from 'src/types/BusinessModel/Aol/BoilersConsumptionV5BM';
import { BoilerConsumptionV5 } from 'src/types/ViewModel/Aol/BoilerConsumptionV5';
import { BoilersUsageTypeEnum } from 'src/types/ViewModel/AolEnums';
import { NoonReportSectionEnum } from 'src/types/BusinessModel/AolEnums';
import { formatDateTime } from 'odfjell-date-time-utils/dateTimeFormatter';
import { mapConsumptionList, mapConsumptionListBM } from './Aol/ConsumptionFactory';
import { PumpFactory } from './PumpFactory';

export class NoonReportMetadataFactory {
    public create(data: NoonReportMetadataBM): NoonReportMetadata {
        const metadata = new NoonReportMetadata(
            data.noonReportId,
            data.legId,
            data.noonReportType,
            convertToDate(data.lastUpdatedUtc),
            data.lastUpdatedBy,
            data.aolCompletedUtc ? convertToDate(data.aolCompletedUtc) : undefined,
            data.aolCompletedBy,
            data.aolApprovedUtc ? convertToDate(data.aolApprovedUtc) : undefined,
            data.aolApprovedBy,
            data.indexInDay,
            data.voyageNumber,
            data.previousPortCallId,
            data.lastPortVisitId,
            data.numberOfReportsInDay,
            data.imosPortCallId,
            data.aolVersion,
            data.isAolComplete,
            data.isAolApproved,
            data.simpleModeAol
        );
        metadata.noonReportGuid = data.noonReportGuid;
        return metadata;
    }
}

export class NoonReportCommentsSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public create(data: NoonReportCommentsSectionBM): NoonReportCommentsSection | undefined {
        if (!data) return;
        return new NoonReportCommentsSection(this.noonReportSectionDataFactory.create(data.sectionData), data.comments);
    }

    public createBM(data: any): NoonReportCommentsSectionBM {
        return {
            comments: data.comments,
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportMrvSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private mrvDataFactory = new MrvDataFactory();

    public create(data?: NoonReportMrvSectionBM): NoonReportMrvSection | undefined {
        if (!data) return;
        return new NoonReportMrvSection(
            this.mrvDataFactory.createList(data.mrvData),
            data.sectionData ? this.noonReportSectionDataFactory.create(data.sectionData) : undefined
        );
    }
}

export class NoonReportFuelRobSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private fuelRobDataFactory = new FuelRobDataFactory();

    public create(data?: NoonReportFuelRobSectionBM): NoonReportFuelRobSection | undefined {
        if (!data) return;
        return new NoonReportFuelRobSection(
            this.fuelRobDataFactory.createList(data.fuelRobData),
            data.sectionData ? this.noonReportSectionDataFactory.create(data.sectionData) : undefined
        );
    }

    public createBM(data: any): NoonReportFuelRobSectionBM {
        return {
            fuelRobData: this.fuelRobDataFactory.createBMList(data.fuelRobData),
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportInertGasProductionSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private nitrogenGeneratorDataFactory = new NitrogenGeneratorDataFactory();

    public create(data?: NoonReportInertGasProductionSectionBM): NoonReportInertGasProductionSection | undefined {
        if (!data) return;
        return new NoonReportInertGasProductionSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            this.nitrogenGeneratorDataFactory.createList(data.nitrogenGenerators),
            data.inertGasGeneratorConsumptionMgo,
            mapConsumptionListBM(data.consumption)
        );
    }
    public createBM(data: any): NoonReportInertGasProductionSectionBM {
        return {
            nitrogenGenerators: this.nitrogenGeneratorDataFactory.createList(data.nitrogenGenerators),
            inertGasGeneratorConsumptionMgo: data.inertGasGeneratorConsumptionMgo,
            consumption: mapConsumptionList(data.consumption),
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportPowerpacksSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private engineDataFactory = new EngineDataFactory();

    public create(data?: NoonReportPowerpacksSectionBM): NoonReportPowerpacksSection | undefined {
        if (!data) return;
        return new NoonReportPowerpacksSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            this.engineDataFactory.createList(data.dieselGasoilDrivenPowerpacks),
            this.engineDataFactory.createList(data.electricalDrivenPowerPacks),
            mapConsumptionListBM(data.consumption)
        );
    }
    public createBM(data: any): NoonReportPowerpacksSectionBM {
        return {
            dieselGasoilDrivenPowerpacks: this.engineDataFactory.createBMList(data.dieselGasoilDrivenPowerpacks),
            electricalDrivenPowerPacks: this.engineDataFactory.createBMList(data.electricalDrivenPowerPacks),
            consumption: mapConsumptionList(data.consumption),
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportFreshWaterSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private evaporatorDataFactory = new EvaporatorDataFactory();

    public create(data?: NoonReportFreshWaterSectionBM): NoonReportFreshWaterSection | undefined {
        if (!data) return;
        return new NoonReportFreshWaterSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.freshWaterDeliveredFromShore,
            data.freshWaterProduction,
            this.evaporatorDataFactory.createList(data.freshWaterEvaporatorData),
            data.freshWaterProductionByReversedOsmosis,
            data.freshWaterUsedForTankCleaning,
            data.freshWaterUsedForDomesticUse,
            data.freshWaterProducedBySteam
        );
    }
    public createBM(data: any): NoonReportFreshWaterSectionBM {
        const freshWaterEvaporatorData = [
            { production: data.freshWaterEvaporatorProduction1, runningHours: data.freshWaterEvaporatorRunningHours1 },
            { production: data.freshWaterEvaporatorProduction2, runningHours: data.freshWaterEvaporatorRunningHours2 },
        ];
        return {
            freshWaterDeliveredFromShore: data.freshWaterDeliveredFromShore,
            freshWaterProduction: data.freshWaterProduction,
            freshWaterEvaporatorData: this.evaporatorDataFactory.createBMList(freshWaterEvaporatorData),
            freshWaterProductionByReversedOsmosis: data.freshWaterProductionByReversedOsmosis,
            freshWaterUsedForTankCleaning: data.freshWaterUsedForTankCleaning,
            freshWaterUsedForDomesticUse: data.freshWaterUsedForDomesticUse,
            freshWaterProducedBySteam: data.freshWaterProducedBySteam,
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportEnginesSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private engineDataFactory = new EngineDataFactory();

    public create(data?: NoonReportEnginesSectionBM): NoonReportEnginesSection | undefined {
        if (!data) return;
        return new NoonReportEnginesSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            this.engineDataFactory.create(data.mainEnginesData),
            this.engineDataFactory.createList(data.auxiliaryEnginesData),
            data.auxiliaryEnginesConsumptionHfo,
            data.auxiliaryEnginesConsumptionMgo,
            this.engineDataFactory.create(data.shaftGeneratorData),
            this.engineDataFactory.createList(data.boilersData),
            data.boilersConsumptionHfo,
            data.boilersConsumptionMgo,
            data.boilersConsumptionForEngineRoomAndAccommodation,
            data.boilersConsumptionForTankCleaning,
            data.boilersConsumptionForCargoHeating,
            data.boilersConsumptionForFreshWaterProduction,
            data.boilersConsumptionForOther,
            data.boilersConsumptionForEngineRoomAndAccommodationMgo,
            data.boilersConsumptionForTankCleaningMgo,
            data.boilersConsumptionForCargoHeatingMgo,
            data.boilersConsumptionForFreshWaterProductionMgo,
            data.boilersConsumptionForOtherMgo,
            data.boilersConsumptionForOtherText,
            data.economizerRunningHours,
            data.economizerDateOfLastCleaning ? convertToDate(data.economizerDateOfLastCleaning) : null,
            data.isEconomizerSootBlowingEquipmentInOperations,
            data.pumpsAndFansNotSwitchedOffReason,
            data.isSurplusSteamAvailable,
            data.isSurplusSteamUsedForFreshWaterProduction,
            data.shorePowerRunningHours,
            data.isShorePowerConnected,
            data.shorePowerKwhReceived,
            data.incineratorConsumptionMgo,
            data.arePumpsAndFansSwitchedOff,
            mapConsumptionListBM(data.consumption),
            this.mapBoilerConsumptionV5ListBM(data.boilerConsumptionV5)
        );
    }

    private mapBoilerConsumptionV5BM(boilerConsumptionV5: BoilerConsumptionV5BM): BoilerConsumptionV5 {
        return {
            BoilerConsumptionId: boilerConsumptionV5.boilerConsumptionId,
            NoonReportId: boilerConsumptionV5.noonReportId,
            BoilersUsageType: boilerConsumptionV5.boilersUsageTypeId as BoilersUsageTypeEnum,
            FuelTypeId: boilerConsumptionV5.fuelTypeId,
            QuantityMt: boilerConsumptionV5.quantityMt,
        };
    }

    private mapBoilerConsumptionV5ListBM(boilerConsumptionV5: BoilerConsumptionV5BM[]): BoilerConsumptionV5[] {
        return boilerConsumptionV5.map((c) => this.mapBoilerConsumptionV5BM(c));
    }

    private mapBoilerConsumptionV5(boilerConsumptionV5: BoilerConsumptionV5): BoilerConsumptionV5BM {
        return {
            boilerConsumptionId: boilerConsumptionV5.BoilerConsumptionId,
            noonReportId: boilerConsumptionV5.NoonReportId ?? 0,
            boilersUsageTypeId: boilerConsumptionV5.BoilersUsageType.valueOf(),
            fuelTypeId: boilerConsumptionV5.FuelTypeId,
            quantityMt: boilerConsumptionV5.QuantityMt,
        };
    }

    private mapBoilerConsumptionV5List(boilerConsumptionV5: BoilerConsumptionV5[]): BoilerConsumptionV5BM[] {
        return boilerConsumptionV5.map((c) => this.mapBoilerConsumptionV5(c));
    }

    public createBM(data: any): NoonReportEnginesSectionBM {
        return {
            mainEnginesData: this.engineDataFactory.createBM(data.mainEngineData),
            auxiliaryEnginesData: this.engineDataFactory.createBMList(data.auxiliaryEnginesData),
            auxiliaryEnginesConsumptionHfo: data.auxiliaryEnginesConsumptionHfo,
            auxiliaryEnginesConsumptionMgo: data.auxiliaryEnginesConsumptionMgo,
            shaftGeneratorData: this.engineDataFactory.createBM(data.shaftGeneratorData),
            boilersData: this.engineDataFactory.createBMList(data.boilersData),
            boilersConsumptionHfo: data.boilersConsumptionHfo,
            boilersConsumptionMgo: data.boilersConsumptionMgo,
            boilersConsumptionForEngineRoomAndAccommodation: data.boilersConsumptionForEngineRoomAndAccommodation,
            boilersConsumptionForTankCleaning: data.boilersConsumptionForTankCleaning,
            boilersConsumptionForCargoHeating: data.boilersConsumptionForCargoHeating,
            boilersConsumptionForFreshWaterProduction: data.boilersConsumptionForFreshWaterProduction,
            boilersConsumptionForOther: data.boilersConsumptionForOther,
            boilersConsumptionForEngineRoomAndAccommodationMgo: data.boilersConsumptionForEngineRoomAndAccommodationMgo,
            boilersConsumptionForTankCleaningMgo: data.boilersConsumptionForTankCleaningMgo,
            boilersConsumptionForCargoHeatingMgo: data.boilersConsumptionForCargoHeatingMgo,
            boilersConsumptionForFreshWaterProductionMgo: data.boilersConsumptionForFreshWaterProductionMgo,
            boilersConsumptionForOtherMgo: data.boilersConsumptionForOtherMgo,
            boilersConsumptionForOtherText: data.boilersConsumptionForOtherText,
            economizerRunningHours: data.economizerRunningHours,
            economizerDateOfLastCleaning: data.economizerDateOfLastCleaning,
            isEconomizerSootBlowingEquipmentInOperations: data.isEconomizerSootBlowingEquipmentInOperationalCondition,
            pumpsAndFansNotSwitchedOffReason: data.pumpsAndFansNotSwitchedOffReason,
            isSurplusSteamAvailable: data.isSurplusSteamAvailable,
            isSurplusSteamUsedForFreshWaterProduction: data.isSurplusSteamUsedForFreshWaterProduction,
            shorePowerRunningHours: data.shorePowerRunningHours,
            isShorePowerConnected: data.isShorePowerConnected,
            shorePowerKwhReceived: data.shorePowerKwhReceived,
            incineratorConsumptionMgo: data.incineratorConsumptionMgo,
            arePumpsAndFansSwitchedOff: data.arePumpsAndFansSwitchedOff,
            sectionData: data.sectionData,
            consumption: data.consumption ? mapConsumptionList(data.consumption) : null,
            boilerConsumptionV5: data.boilerConsumptionV5 ? this.mapBoilerConsumptionV5List(data.boilerConsumptionV5) : null,
        };
    }
}

export class NoonReportCargoHeatingSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private heaterFactory = new HeaterFactory();
    private cargoTemperatureFactory = new CargoTemperatureFactory();
    private cargoTankTemperatureFactory = new CargoTankTemperatureFactory();
    private pumpFactory = new PumpFactory();

    public create(data?: NoonReportCargoHeatingSectionBM): NoonReportCargoHeatingSection | undefined {
        if (!data) return;
        return new NoonReportCargoHeatingSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.directHeating,
            data.heatingFromAdjacentTanks,
            data.indirectHeating,
            data.cargoCirculatingHeating,
            this.heaterFactory.createList(data.hotWaterCargoHeaters),
            this.cargoTemperatureFactory.createList(data.cargoTemperatures),
            this.cargoTankTemperatureFactory.createList(data.cargoTankTemperatures),
            this.pumpFactory.createList(data.circulatingPumpsForCargoHeating)
        );
    }

    public createBM(data: any): NoonReportCargoHeatingSectionBM {
        const cargoTankTemperatures = (data.inhibitedCargoTankTemperatures ?? [])
            .concat(data.heatedAndCooledCargoTankTemperatures ?? [])
            .concat(data.otherCargoTankTemperatures ?? []);

        return {
            directHeating: data.directHeating,
            indirectHeating: data.indirectHeating,
            heatingFromAdjacentTanks: data.heatingFromAdjacentTanks,
            cargoCirculatingHeating: data.cargoCirculatingHeating,
            hotWaterCargoHeaters: [
                this.heaterFactory.createBM(1, data.waterInletTemperatureHeater1, data.waterOutletTemperatureHeater1),
                this.heaterFactory.createBM(2, data.waterInletTemperatureHeater2, data.waterOutletTemperatureHeater2),
            ],
            cargoTemperatures: [],
            cargoTankTemperatures: cargoTankTemperatures?.map(this.cargoTankTemperatureFactory.createBM) ?? [],
            circulatingPumpsForCargoHeating: [
                { pumpId: 1, runningHours: data.circulatingPumpsForCargoHeatingRunningHours1 },
                { pumpId: 2, runningHours: data.circulatingPumpsForCargoHeatingRunningHours2 },
            ],
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportTankCleaningSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public create(data?: NoonReportTankCleaningSectionBM): NoonReportTankCleaningSection | undefined {
        if (!data) return;
        return new NoonReportTankCleaningSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.tankCleaningPumpFreshWaterRunningHours,
            data.tankCleaningPumpSeaWaterRunningHours,
            data.tankCleaningOperationSinceLastReport,
            data.tankCleaningDuration,
            data.tankCleanerHeatersHoursWithHotWater,
            data.tankCleanerHeatersHoursWithColdWater
        );
    }

    public createBM(data: any): NoonReportTankCleaningSectionBM {
        return {
            tankCleaningOperationSinceLastReport: data.tankCleaningOperationSinceLastReport,
            tankCleaningDuration: data.tankCleaningDuration,
            tankCleanerHeatersHoursWithHotWater: data.tankCleanerHeatersHoursWithHotWater,
            tankCleanerHeatersHoursWithColdWater: data.tankCleanerHeatersHoursWithColdWater,
            tankCleaningPumpFreshWaterRunningHours: data.tankCleaningPumpFreshWaterRunningHours,
            tankCleaningPumpSeaWaterRunningHours: data.tankCleaningPumpSeaWaterRunningHours,
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportWeatherSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public create(data?: NoonReportWeatherSectionBM): NoonReportWeatherSection | undefined {
        if (!data) return;
        return new NoonReportWeatherSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.badWeatherHours,
            data.currentForce,
            data.waveHeight,
            data.seaDirection,
            data.windDirection,
            data.windForce,
            data.airTemperature,
            data.seaTemperature,
            data.currentDirection,
            data.seaState
        );
    }
    public createBM(data: any): NoonReportWeatherSectionBM {
        return {
            badWeatherHours: data.badWeatherHours,
            airTemperature: data.airTemperature,
            seaTemperature: data.seaTemperature,
            currentForce: data.currentForce,
            waveHeight: data.waveHeight,
            seaState: getSeaStateEnum(data.seaState),
            seaDirection: getDirectionEnum(data.seaDirection),
            windForce: getWindForceEnum(data.windForce),
            windDirection: getDirectionEnum(data.windDirection),
            currentDirection: getDirectionEnum(data.currentDirection),
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportBridgeSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public create(data?: NoonReportBridgeSectionBM): NoonReportBridgeSection | undefined {
        if (!data) return;
        return new NoonReportBridgeSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.loggedSpeed,
            data.loggedDistance,
            data.observedSpeed,
            data.observedDistance,
            data.ecaZoneHours,
            data.forwardDraft,
            data.aftDraft,
            data.navtorDistance,
            data.isNavtorDistanceValid
        );
    }
    public createBM(data: any): NoonReportBridgeSectionBM {
        return {
            loggedSpeed: data.loggedSpeed,
            loggedDistance: data.loggedDistance,
            observedSpeed: data.observedSpeed,
            navtorDistance: data.navtorDistance ?? null,
            isNavtorDistanceValid: data.isNavtorDistanceValid ?? false,
            observedDistance: data.observedDistance,
            ecaZoneHours: data.ecaZoneHours,
            forwardDraft: data.forwardDraft,
            aftDraft: data.aftDraft,
            sectionData: data.sectionData,
        };
    }
}

export class NoonReportActivitiesSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public create(data?: NoonReportActivitiesSectionBM): NoonReportActivitiesSection | undefined {
        if (!data) return;
        return new NoonReportActivitiesSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.dryDockingPortActivityHours,
            data.loadingPortActivityHours,
            data.unloadingPortActivityHours,
            data.shiftingAndMooringPortActivityHours,
            data.idlePortActivityHours,
            data.anchorPortActivityHours,
            data.steamingForTankCleaningPortActivityHours,
            data.canalTransitPortActivityHours,
            data.offHireHours,
            data.awaitingOrderOrDriftingHours,
            data.speedOrderByOperatorsHours,
            data.standardActivitiesAtSeaHours,
            data.otherExclusionsHours,
            data.operationalTargetSpeedHours,
            data.operationalTargetConsumptionHours,
            data.operationalNonTargetHours,
            data.operationalAwaitingOrdersOrDriftingHours,
            data.operationalTankPreparationHours,
            data.navigationalEcaZoneHours,
            data.navigationalCasualtyAndSarHours,
            data.navigationalHighRiskAreaHours,
            data.navigationalPilotManeuveringHours,
            data.navigationalRiverPortManeuveringHours,
            data.navigationalSafetyManeuveringHours,
            data.navigationalStormShelteringHours,
            data.navigationalTrafficManeuveringHours,
            data.navigationalCasualtyAndSarDrillHours,
            data.technicalAuxiliarySystemFailureHours,
            data.technicalFuelQualityIssueHours,
            data.technicalPropulsionSystemFailureHours,
            data.technicalOtherTechnicalIssueHours,
            data.unscheduledStoppagesHours,
            data.speedOrderHours,
            data.offHirePortActivityHours
        );
    }
    public createBM(data: any): NoonReportActivitiesSectionBM {
        return {
            dryDockingPortActivityHours: data.dryDockingPortActivityHours,
            loadingPortActivityHours: data.loadingPortActivityHours,
            unloadingPortActivityHours: data.unloadingPortActivityHours,
            shiftingAndMooringPortActivityHours: data.shiftingAndMooringPortActivityHours,
            idlePortActivityHours: data.idlePortActivityHours,
            anchorPortActivityHours: data.anchorPortActivityHours,
            steamingForTankCleaningPortActivityHours: data.steamingForTankCleaningPortActivityHours,
            canalTransitPortActivityHours: data.canalTransitPortActivityHours,

            awaitingOrderOrDriftingHours: data.awaitingOrderOrDriftingHours,
            standardActivitiesAtSeaHours: data.standardActivitiesAtSeaHours,
            otherExclusionsHours: data.otherExclusionsHours,
            offHireHours: data.offHireHours,
            speedOrderByOperatorsHours: data.speedOrderByOperatorsHours,

            operationalTargetSpeedHours: data.operationalTargetSpeedHours,
            operationalTargetConsumptionHours: data.operationalTargetConsumptionHours,
            operationalNonTargetHours: data.operationalNonTargetHours,
            operationalAwaitingOrdersOrDriftingHours: data.operationalAwaitingOrdersOrDriftingHours,
            operationalTankPreparationHours: data.operationalTankPreparationHours,

            navigationalEcaZoneHours: data.navigationalEcaZoneHours,
            navigationalCasualtyAndSarHours: data.navigationalCasualtyAndSarHours,
            navigationalHighRiskAreaHours: data.navigationalHighRiskAreaHours,
            navigationalPilotManeuveringHours: data.navigationalPilotManeuveringHours,
            navigationalRiverPortManeuveringHours: data.navigationalRiverPortManeuveringHours,
            navigationalSafetyManeuveringHours: data.navigationalSafetyManeuveringHours,
            navigationalStormShelteringHours: data.navigationalStormShelteringHours,
            navigationalTrafficManeuveringHours: data.navigationalTrafficManeuveringHours,
            navigationalCasualtyAndSarDrillHours: data.navigationalCasualtyAndSarDrillHours,

            technicalAuxiliarySystemFailureHours: data.technicalAuxiliarySystemFailureHours,
            technicalFuelQualityIssueHours: data.technicalFuelQualityIssueHours,
            technicalPropulsionSystemFailureHours: data.technicalPropulsionSystemFailureHours,
            technicalOtherTechnicalIssueHours: data.technicalOtherTechnicalIssueHours,

            unscheduledStoppagesHours: data.unscheduledStoppagesHours,
            speedOrderHours: data.speedOrderHours,

            sectionData: data.sectionData,
        };
    }
}

export class NoonReportGeneralSectionFactory {
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();
    private noonReportDateTimeFactory = new NoonReportDateTimeFactory();

    public update(section: NoonReportGeneralSection, data: any) {
        section.ballast = data.ballast;
        section.idlOffsetHours = data.idlOffsetHours;
        section.manualOffsetHours = data.manualOffsetHours;
        section.cargoOnBoard = data.cargoOnBoard;
    }

    public create(data: NoonReportGeneralSectionBM): NoonReportGeneralSection {
        return new NoonReportGeneralSection(
            this.noonReportSectionDataFactory.create(data.sectionData),
            data.hours,
            data.idlOffsetHours,
            data.manualOffsetHours,
            this.noonReportDateTimeFactory.create(data.noonReportDateTime),
            data.cargoOnBoard,
            data.ballast
        );
    }
    public createBM(data: any): NoonReportGeneralSectionBM {
        return {
            cargoOnBoard: data.cargoOnBoard,
            ballast: data.ballast,
            hours: data.hours,
            idlOffsetHours: data.idlOffsetHours,
            manualOffsetHours: data.manualOffsetHours,
            noonReportDateTime: new NoonReportDateTimeFactory().createBM(data),
            sectionData: data.sectionData,
        };
    }
}

export class ValidatedNoonReportFactory {
    private noonReportMetadataFactory = new NoonReportMetadataFactory();
    private noonReportGeneralSectionFactory = new NoonReportGeneralSectionFactory();
    private noonReportActivitiesSectionFactory = new NoonReportActivitiesSectionFactory();
    private noonReportBridgeSectionFactory = new NoonReportBridgeSectionFactory();
    private noonReportWeatherSectionFactory = new NoonReportWeatherSectionFactory();
    private noonReportTankCleaningSectionFactory = new NoonReportTankCleaningSectionFactory();
    private noonReportCargoHeatingSectionFactory = new NoonReportCargoHeatingSectionFactory();
    private noonReportEnginesSectionFactory = new NoonReportEnginesSectionFactory();
    private noonReportFreshWaterSectionFactory = new NoonReportFreshWaterSectionFactory();
    private noonReportPowerpacksSectionFactory = new NoonReportPowerpacksSectionFactory();
    private noonReportInertGasProductionSectionFactory = new NoonReportInertGasProductionSectionFactory();
    private noonReportMrvSectionFactory = new NoonReportMrvSectionFactory();
    private noonReportFuelRobSectionFactory = new NoonReportFuelRobSectionFactory();
    private noonReportCommentsSectionFactory = new NoonReportCommentsSectionFactory();
    private noonReportDateTimeFactory = new NoonReportDateTimeFactory();
    private noonReportSectionDataFactory = new NoonReportSectionDataFactory();

    public updateOfflineReport = (noonReport: ValidatedNoonReport, data: any) => {
        if (data.sectionData.section === NoonReportSectionEnum.General) {
            this.noonReportGeneralSectionFactory.update(noonReport.generalSection, data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Activities) {
            noonReport.activitiesSection = this.noonReportActivitiesSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Bridge) {
            noonReport.bridgeSection = this.noonReportBridgeSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Weather) {
            noonReport.weatherSection = this.noonReportWeatherSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.TankCleaning) {
            noonReport.tankCleaningSection = this.noonReportTankCleaningSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.CargoTemperatureControl) {
            noonReport.cargoHeatingSection = this.noonReportCargoHeatingSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Engines) {
            noonReport.enginesSection = this.noonReportEnginesSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.FreshWater) {
            noonReport.freshWaterSection = this.noonReportFreshWaterSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.PowerPacks) {
            noonReport.powerpacksSection = this.noonReportPowerpacksSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.InertGasProduction) {
            noonReport.inertGasProductionSection = this.noonReportInertGasProductionSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Mrv) {
            noonReport.mrvSection = this.noonReportMrvSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.FuelRob) {
            noonReport.fuelRobSection = this.noonReportFuelRobSectionFactory.create(data);
        }
        if (data.sectionData.section === NoonReportSectionEnum.Comments) {
            noonReport.commentsSection = this.noonReportCommentsSectionFactory.create(data);
        }

        if (!noonReport.hasOfflineData) noonReport.hasOfflineData = true;
        return noonReport;
    };

    public createNewOfflineReport = (data: any) => {
        const aolDateLocalTime = getAolDateFromTime(data.reportFromTimeLocalTime);
        data.aolDateLocalTime = formatDateTime(aolDateLocalTime, 'yyyy-MM-dd');
        data.hours = +data.aolHours;
        data.aolVersion = Number(data.aolVersion) || 5;
        data.section = NoonReportSectionEnum.General;
        if (!data.lastUpdatedUtc) {
            data.lastUpdatedUtc = new Date().toISOString();
        }
        if (!data.lastUpdatedBy) {
            data.lastUpdatedBy = '';
        }

        data.noonReportDateTime = this.noonReportDateTimeFactory.create(data);
        const metaDataBM = this.noonReportMetadataFactory.create(data);
        const generalSectionBM = this.noonReportGeneralSectionFactory.create(data);
        generalSectionBM.sectionData = this.noonReportSectionDataFactory.create(data);

        const noonReport = new ValidatedNoonReport(metaDataBM, generalSectionBM);
        noonReport.hasOfflineData = true;
        return noonReport;
    };

    public create(data: ValidatedNoonReportBM): ValidatedNoonReport {
        return new ValidatedNoonReport(
            this.noonReportMetadataFactory.create(data.metadata),
            this.noonReportGeneralSectionFactory.create(data.generalSection),
            this.noonReportActivitiesSectionFactory.create(data.activitiesSection),
            this.noonReportBridgeSectionFactory.create(data.bridgeSection),
            this.noonReportWeatherSectionFactory.create(data.weatherSection),
            this.noonReportTankCleaningSectionFactory.create(data.tankCleaningSection),
            this.noonReportCargoHeatingSectionFactory.create(data.cargoHeatingSection),
            this.noonReportEnginesSectionFactory.create(data.enginesSection),
            this.noonReportFreshWaterSectionFactory.create(data.freshWaterSection),
            this.noonReportPowerpacksSectionFactory.create(data.powerpacksSection),
            this.noonReportInertGasProductionSectionFactory.create(data.inertGasProductionSection),
            this.noonReportMrvSectionFactory.create(data.mrvSection),
            this.noonReportFuelRobSectionFactory.create(data.fuelRobSection),
            this.noonReportCommentsSectionFactory.create(data.commentsSection)
        );
    }

    public createList(data: ValidatedNoonReportBM[]): ValidatedNoonReport[] {
        return data.filter((o) => o?.metadata != null && o?.generalSection != null).map((h) => this.create(h));
    }
}
